/**
 * Shared props for decimal inputs in the Polish-first UI.
 *
 * Ant Design treats a comma as a removable character unless the input is
 * configured explicitly. That turns an operator entry such as `156,25` into
 * `15625`, which is especially dangerous for quantities and costs.
 */
export const parsePolishDecimalInput = (displayValue?: string): string => (
  String(displayValue ?? '')
    .replace(/\s/g, '')
    .replace(',', '.')
);

export const POLISH_DECIMAL_INPUT_NUMBER_PROPS = {
  decimalSeparator: ',',
} as const;

/**
 * A quantity input that shows its value like every other quantity — `100`,
 * `12,5`, not `100.000` (follow-up v3.16): Polish comma, trailing zeros of
 * `precision` dropped while the operator is not typing.
 */
export const formatPolishQuantityInput = (value?: string | number | null): string => {
  const normalized = String(value ?? '').replace('.', ',');
  if (!normalized.includes(',')) return normalized;
  return normalized.replace(/0+$/, '').replace(/,$/, '');
};

export const POLISH_QUANTITY_INPUT_NUMBER_PROPS = {
  ...POLISH_DECIMAL_INPUT_NUMBER_PROPS,
  formatter: (value: string | number | undefined, info: { userTyping: boolean; input: string }) => (
    info.userTyping ? info.input : formatPolishQuantityInput(value)
  ),
} as const;

/**
 * A money amount shown like every amount (`5 000,00`, not `5000.00`): Polish
 * comma and grouped thousands while the operator is not typing; the parser
 * accepts spaces and a decimal comma (PR3 review, „Koszty stałe”).
 */
export const formatPolishMoneyInput = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === '') return '';
  const numeric = Number(value);
  // Always two decimals, like every amount on screen.
  const [whole, fraction] = (Number.isFinite(numeric) ? numeric.toFixed(2) : String(value)).split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${fraction !== undefined ? `,${fraction}` : ''}`;
};

export const POLISH_MONEY_INPUT_NUMBER_PROPS = {
  formatter: (value: string | number | undefined, info: { userTyping: boolean; input: string }) => (
    info.userTyping ? info.input : formatPolishMoneyInput(value)
  ),
  parser: (displayValue?: string) => parsePolishDecimalInput(displayValue),
} as const;
